import { roleLabel } from 'common/share-dialog';
import { vaultInvitationRole } from 'helpers/sharing';
import { LockIcon, VaultsIcon } from 'assets/icons';
import { CreateVaultSteps } from 'common/enums';
import { formatPrice, sizeLabel } from '../../plan-format';
import { VaultNewStateType } from '../../vault-new-reducer';
import styles from './step-confirmation.module.scss';

type StepConfirmationProps = {
  newVaultState: VaultNewStateType;
  onEditStep: (step: CreateVaultSteps) => void;
};

const initialOf = (value: string) =>
  value.trim().charAt(0).toUpperCase() || '?';

// Step 3: review everything, then hand off to Stripe. No card details are
// collected here — payment happens on Stripe's hosted page, which is also
// where discount codes are entered.
const StepConfirmation: React.FC<StepConfirmationProps> = ({
  newVaultState,
  onEditStep,
}) => {
  const { vault, planSelected, coverPreviewUrl } = newVaultState;

  const editors = vault.contributors.filter(
    (contributor) => contributor.email.trim().length > 0,
  );

  const showEditButton = (step: CreateVaultSteps) => (
    <button
      type="button"
      className={styles.editButton}
      onClick={() => onEditStep(step)}
    >
      Edit
    </button>
  );

  const showVaultSection = () => (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Vault</h2>
        {showEditButton(CreateVaultSteps.information)}
      </div>

      <div className={styles.vaultRow}>
        <div className={styles.vaultAvatar}>
          {coverPreviewUrl ? (
            <img src={coverPreviewUrl} alt="" />
          ) : (
            <VaultsIcon />
          )}
        </div>
        <span className={styles.vaultTitle}>
          {vault.title.trim() || 'Untitled vault'}
        </span>
      </div>

      {editors.length > 0 && (
        <>
          <ul className={styles.editorList}>
            {editors.map((editor) => (
              <li key={editor.email} className={styles.editorItem}>
                <span className={styles.editorAvatar}>
                  {initialOf(editor.name || editor.email)}
                </span>
                <span className={styles.editorName}>
                  {editor.name.trim() || editor.email}
                </span>
                <span className={styles.editorBadge}>
                  {roleLabel(vaultInvitationRole(editor))}
                </span>
              </li>
            ))}
          </ul>
          <p className={styles.editorNote}>Invite sends after payment.</p>
        </>
      )}
    </section>
  );

  const showPlanSection = () => {
    if (!planSelected) return null;

    const kindLabel = planSelected.kind === 'long_term' ? 'One-time' : 'Annual';

    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Plan</h2>
          {showEditButton(CreateVaultSteps.plan)}
        </div>

        <div className={styles.planRow}>
          <span className={styles.planName}>
            {`${kindLabel} · ${sizeLabel(planSelected.storageGb)}`}
          </span>
          <span className={styles.planPrice}>
            {formatPrice(planSelected.price, planSelected.currency)}
          </span>
        </div>

        {planSelected.mediaEstimate && (
          <p className={styles.planEstimate}>{planSelected.mediaEstimate}</p>
        )}

        <div className={styles.planRow}>
          <span className={styles.discountLabel}>Discount</span>
          <span className={styles.discountHint}>Added at checkout</span>
        </div>
      </section>
    );
  };

  const showTotal = () => {
    if (!planSelected) return null;

    return (
      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>Total due today</span>
        <span className={styles.totalAmount}>
          {formatPrice(planSelected.price, planSelected.currency)}
        </span>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.panel}>
        {showVaultSection()}
        {showPlanSection()}
        {showTotal()}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Payment</h2>

          <div className={styles.stripeBox}>
            <LockIcon className={styles.stripeIcon} />
            <div className={styles.stripeText}>
              <p className={styles.stripeTitle}>Secure checkout by Stripe</p>
              <p className={styles.stripeBody}>
                Cards, Apple Pay, Google Pay, and discount codes on the next
                screen — then you&apos;ll land straight in your new vault.
              </p>
            </div>
          </div>
        </section>

        <p className={styles.terms}>
          By continuing to payment, you agree to the{' '}
          <a href="/terms-of-service" className={styles.termsLink}>
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" className={styles.termsLink}>
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </div>
  );
};

export { StepConfirmation };
